// Masked trip comms: the company's TRIP-LINE pool, shown in Business schedule → Calls and phone line.
//
// Plain English: trip lines are extra Twilio numbers, used only to connect a driver and a client during a trip. Both
// sides see the trip line, never each other's real number. The company main line(s) are never used for this, and a
// number can never be both. Owners/admins register numbers already bought in Twilio, switch them on or off, and see which
// trips each line is serving right now. Guidance: 2–4 lines per company (default target 3). Nothing here buys numbers or
// changes the Twilio console.
//
// Off = draining: trips already on the line keep calling and texting through it until they close; it gets no new trips.
// A line's number cannot be edited (remove it and add the new number). Removing a line that carried trips archives it
// (off for good, history kept); an archived line can be restored.
import React, { useCallback, useEffect, useState } from 'react';
import Switch from '../ui/Switch';
import Button3D from '../ui/Button3D';
import ActionPillMenu from '../ui/ActionPillMenu';
import { D2_INPUT, D2_SECTION_TITLE } from '../settings/signatures/d2Fields';
import { formatPhone } from '../../lib/phone';

const E164 = /^\+[1-9]\d{6,14}$/;
const RECOMMENDED_MIN = 2;
const RECOMMENDED_MAX = 4;

export function tripLineWebhookUrls(supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '') {
    const base = `${String(supabaseUrl).replace(/\/$/, '')}/functions/v1/trip-line-webhook`;
    return { sms: `${base}?action=sms`, voice: `${base}?action=voice` };
}

export function poolGuidance(enabledCount, target) {
    if (enabledCount === 0) return { tone: 'warn', text: `No trip lines yet. Register ${target} so drivers and clients can reach each other without seeing real numbers.` };
    if (enabledCount < RECOMMENDED_MIN) return { tone: 'warn', text: `Only ${enabledCount} trip line is on. ${RECOMMENDED_MIN}–${RECOMMENDED_MAX} are recommended (target ${target}); with one line, a client with two trips at once can only be masked on one.` };
    if (enabledCount > RECOMMENDED_MAX) return { tone: 'warn', text: `${enabledCount} trip lines are on. ${RECOMMENDED_MIN}–${RECOMMENDED_MAX} is usually enough; each extra number is a monthly Twilio cost.` };
    if (enabledCount < target) return { tone: 'info', text: `${enabledCount} of your target ${target} trip lines are on.` };
    return { tone: 'ok', text: `${enabledCount} trip line${enabledCount === 1 ? '' : 's'} on — within the recommended ${RECOMMENDED_MIN}–${RECOMMENDED_MAX}.` };
}

function friendlyError(error, fallback) {
    const message = String(error?.message || '');
    if (/company main line|already a company line/i.test(message)) return 'That number is a company line. Company main lines can never be trip lines.';
    if (/org_trip_lines_phone_unique|duplicate key/i.test(message)) return 'That number is already registered as a trip line (it may be archived — restore it from the list).';
    if (/cannot be changed/i.test(message)) return 'A trip line\u2019s number cannot be changed. Remove the line and add the new number instead.';
    if (/trip history|foreign key|violates/i.test(message)) return 'This line has trip history, so it is archived instead of deleted (off for good, history kept).';
    if (/row-level security|permission|42501/i.test(message)) return 'Only a company owner or admin can change trip lines.';
    return fallback;
}

export default function TripLinePoolSettings({ orgId, client }) {
    const [lines, setLines] = useState(null);
    const [settings, setSettings] = useState(null);
    const [form, setForm] = useState({ phone: '', label: '', accountSid: '', messagingServiceSid: '' });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const urls = tripLineWebhookUrls();

    const load = useCallback(async () => {
        if (!orgId || !client?.rpc) return;
        const [{ data: pool, error: poolError }, { data: row }] = await Promise.all([
            client.rpc('trip_comms_pool_status'),
            client.from('org_trip_line_settings')
                .select('masking_enabled, target_pool_size, close_after_minutes, max_session_hours, company_display_name, public_main_line')
                .eq('org_id', orgId).maybeSingle(),
        ]);
        if (poolError) { setError('Could not load the trip lines. Only a company owner or admin can manage them.'); setLines([]); return; }
        setLines(Array.isArray(pool) ? pool : []);
        setSettings({
            masking_enabled: row?.masking_enabled !== false,
            target_pool_size: row?.target_pool_size ?? 3,
            close_after_minutes: row?.close_after_minutes ?? 60,
            max_session_hours: row?.max_session_hours ?? 12,
            company_display_name: row?.company_display_name ?? '',
            public_main_line: row?.public_main_line ?? '',
        });
    }, [client, orgId]);

    useEffect(() => { load(); }, [load]);

    async function saveSettings(change) {
        const next = { ...settings, ...change };
        setBusy(true); setError(''); setNotice('');
        setSettings(next);
        const { error: saveError } = await client.from('org_trip_line_settings').upsert({
            org_id: orgId,
            masking_enabled: next.masking_enabled,
            target_pool_size: Number(next.target_pool_size) || 3,
            close_after_minutes: Number(next.close_after_minutes),
            max_session_hours: Number(next.max_session_hours) || 12,
            company_display_name: next.company_display_name?.trim() || null,
            public_main_line: next.public_main_line?.trim() || null,
        }, { onConflict: 'org_id' });
        setBusy(false);
        if (saveError) { await load(); setError(friendlyError(saveError, 'Could not save. Check the values and try again.')); return; }
        setNotice('Trip-line settings saved.');
    }

    async function register(event) {
        event.preventDefault();
        const phone = form.phone.replace(/[\s().-]/g, '');
        if (!E164.test(phone)) { setError('Enter the trip line with its country code, like +14045551234.'); return; }
        if (form.accountSid && !/^AC[0-9a-fA-F]{32}$/.test(form.accountSid.trim())) { setError('The Twilio account SID starts with AC and has 34 characters.'); return; }
        if (form.messagingServiceSid && !/^MG[0-9a-fA-F]{32}$/.test(form.messagingServiceSid.trim())) { setError('The Messaging Service SID starts with MG and has 34 characters.'); return; }
        setBusy(true); setError(''); setNotice('');
        const { error: insertError } = await client.from('org_trip_lines').insert({
            org_id: orgId,
            phone_number: phone,
            label: form.label.trim() || null,
            twilio_account_sid: form.accountSid.trim() || null,
            messaging_service_sid: form.messagingServiceSid.trim() || null,
        });
        setBusy(false);
        if (insertError) { setError(friendlyError(insertError, 'Could not register that trip line.')); return; }
        setForm({ phone: '', label: '', accountSid: '', messagingServiceSid: '' });
        setNotice(`${formatPhone(phone) || phone} registered as a trip line.`);
        await load();
    }

    async function setEnabled(line, enabled) {
        setBusy(true); setError(''); setNotice('');
        const { error: updateError } = await client.from('org_trip_lines').update({ enabled }).eq('id', line.id).eq('org_id', orgId);
        setBusy(false);
        if (updateError) { setError(friendlyError(updateError, 'Could not change that trip line.')); return; }
        setNotice(enabled ? 'Trip line turned on.' : 'Trip line turned off: finishing its current trips, no new trips.');
        await load();
    }

    async function restore(line) {
        setBusy(true); setError(''); setNotice('');
        const { error: updateError } = await client.from('org_trip_lines').update({ archived_at: null, enabled: true }).eq('id', line.id).eq('org_id', orgId);
        setBusy(false);
        if (updateError) { setError(friendlyError(updateError, 'Could not restore that trip line.')); return; }
        setNotice('Trip line restored and turned on.');
        await load();
    }

    async function remove(line) {
        setBusy(true); setError(''); setNotice('');
        const { data, error: removeError } = await client.rpc('trip_comms_remove_line', { p_line_id: line.id });
        setBusy(false);
        if (removeError) { setError(friendlyError(removeError, 'Could not remove that trip line.')); return; }
        setNotice(data?.message || 'Trip line removed from the pool.');
        await load();
    }

    if (!client?.rpc) return null;
    if (!lines && !error) return <p className="text-sm text-[var(--ink-muted)]" aria-busy="true">Loading trip lines…</p>;
    const enabledCount = (lines || []).filter((line) => line.enabled).length;
    const lineState = (line) => {
        if (line.archived_at) return line.serving?.length ? 'Archived: finishing current trips, no new trips' : 'Archived (history kept)';
        return line.enabled ? 'On' : 'Disabled: finishing current trips, no new trips';
    };
    const guidance = poolGuidance(enabledCount, settings?.target_pool_size ?? 3);
    const toneClass = guidance.tone === 'warn'
        ? 'border-amber-500/60 bg-amber-500/10 text-amber-900 dark:text-amber-100'
        : 'border-[var(--border)] text-[var(--ink-muted)]';

    return (
        <div className="space-y-3 border-t border-[var(--border)] pt-4" data-testid="trip-line-pool">
            <h4 className={D2_SECTION_TITLE}>Trip lines (masked driver ↔ client calls and texts)</h4>
            <p className="text-xs text-[var(--ink-muted)]">
                When a driver accepts a trip, the trip gets one of these numbers. The client and the driver call or text that number and
                are connected to each other — neither sees the other&apos;s real number. Your main line is never used for this.
            </p>
            {error && <p role="alert" className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm">{error}</p>}
            <p role="status" data-testid="trip-line-guidance" className={`rounded-lg border p-3 text-sm ${toneClass}`}>{guidance.text}</p>

            {settings && (
                <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-[var(--border)] px-3 py-2">
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold">Masked calling and texting</p>
                        <p className="text-xs text-[var(--ink-muted)]">On: each accepted trip is paired with a free trip line. Off: no new pairings.</p>
                    </div>
                    <Switch label="Masked calling and texting" checked={settings.masking_enabled} busy={busy}
                        onChange={(on) => saveSettings({ masking_enabled: on })} />
                </div>
            )}

            <ul className="space-y-2" aria-label="Trip lines">
                {(lines || []).map((line) => (
                    <li key={line.id} className="rounded-lg border border-[var(--border)] px-3 py-2" data-testid="trip-line-row">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="min-w-0">
                                <p className="text-sm font-bold">{formatPhone(line.phone_number) || line.phone_number}{line.label ? ` · ${line.label}` : ''}</p>
                                <p className="text-xs text-[var(--ink-muted)]">
                                    <span data-testid="trip-line-state">{lineState(line)}</span> · {line.serving?.length
                                        ? `Serving ${line.serving.length} trip${line.serving.length === 1 ? '' : 's'}: ${line.serving.map((trip) => trip.confirmation_number).join(', ')}`
                                        : 'Not serving any trip right now'}
                                </p>
                            </div>
                            <ActionPillMenu
                                menuLabel={`Trip line ${line.phone_number} actions`}
                                triggerLabel={`Manage trip line ${line.phone_number}`}
                                options={line.archived_at ? [
                                    { key: 'restore', label: 'Restore and turn on', disabled: busy, onSelect: () => restore(line) },
                                ] : [
                                    { key: 'toggle', label: line.enabled ? 'Turn off (finish current trips)' : 'Turn on', disabled: busy, onSelect: () => setEnabled(line, !line.enabled) },
                                    { key: 'remove', label: 'Remove from pool', disabled: busy || line.serving?.length > 0, onSelect: () => remove(line) },
                                ]}
                            >
                                Manage
                            </ActionPillMenu>
                        </div>
                    </li>
                ))}
            </ul>

            <form className="grid gap-2 rounded-lg border border-[var(--border)] p-3 md:grid-cols-2" onSubmit={register} aria-label="Register a trip line">
                <p className="text-sm font-bold md:col-span-2">Register a number already in your Twilio account</p>
                <p className="text-xs text-[var(--ink-muted)] md:col-span-2">A trip line&apos;s number can&apos;t be edited later. To change it, remove the line and add the new number.</p>
                <label className="flex flex-col gap-1 text-xs">Trip line number
                    <input className={D2_INPUT} value={form.phone} placeholder="+14045551234" inputMode="tel"
                        onChange={(event) => setForm({ ...form, phone: event.target.value })} />
                </label>
                <label className="flex flex-col gap-1 text-xs">Label (optional)
                    <input className={D2_INPUT} value={form.label} maxLength={80} placeholder="Trip line 1"
                        onChange={(event) => setForm({ ...form, label: event.target.value })} />
                </label>
                <label className="flex flex-col gap-1 text-xs">Twilio account SID (optional, if not the default account)
                    <input className={D2_INPUT} value={form.accountSid} placeholder="AC…"
                        onChange={(event) => setForm({ ...form, accountSid: event.target.value })} />
                </label>
                <label className="flex flex-col gap-1 text-xs">A2P Messaging Service SID (for your records)
                    <input className={D2_INPUT} value={form.messagingServiceSid} placeholder="MG…"
                        onChange={(event) => setForm({ ...form, messagingServiceSid: event.target.value })} />
                </label>
                <div className="md:col-span-2">
                    <Button3D type="submit" variant="go" disabled={busy}>Register trip line</Button3D>
                </div>
            </form>

            {settings && (
                <div className="grid gap-2 rounded-lg border border-[var(--border)] p-3 md:grid-cols-2">
                    <label className="flex flex-col gap-1 text-xs">Target number of trip lines
                        <input className={D2_INPUT} type="number" min={1} max={10} value={settings.target_pool_size}
                            onChange={(event) => setSettings({ ...settings, target_pool_size: event.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">Minutes the line stays open after a trip ends
                        <input className={D2_INPUT} type="number" min={0} max={1440} value={settings.close_after_minutes}
                            onChange={(event) => setSettings({ ...settings, close_after_minutes: event.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">Close any line still open this many hours after the trip&apos;s scheduled end (safety net)
                        <input className={D2_INPUT} type="number" min={1} max={72} value={settings.max_session_hours}
                            onChange={(event) => setSettings({ ...settings, max_session_hours: event.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">Company name in the &quot;this number is for trips&quot; notice and on driver texts
                        <input className={D2_INPUT} value={settings.company_display_name} maxLength={80}
                            onChange={(event) => setSettings({ ...settings, company_display_name: event.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">Main line to point people to (only said or written, never used to connect)
                        <input className={D2_INPUT} value={settings.public_main_line} placeholder="+14045942667"
                            onChange={(event) => setSettings({ ...settings, public_main_line: event.target.value })} />
                    </label>
                    <div className="md:col-span-2">
                        <Button3D disabled={busy} onClick={() => saveSettings({})}>Save trip-line settings</Button3D>
                    </div>
                </div>
            )}

            <details className="rounded-lg border border-[var(--border)] p-3 text-xs">
                <summary className="cursor-pointer font-bold">Twilio setup for each trip line</summary>
                <p className="mt-2">In the Twilio console, open the number and set:</p>
                <p className="mt-1">A message comes in (HTTP POST): <code className="break-all" data-testid="trip-line-sms-url">{urls.sms}</code></p>
                <p className="mt-1">A call comes in (HTTP POST): <code className="break-all" data-testid="trip-line-voice-url">{urls.voice}</code></p>
                <p className="mt-1">Then add the number to your A2P 10DLC Messaging Service. Leave the Messaging Service&apos;s own &quot;incoming messages&quot; setting on &quot;Defer to sender&quot; so the number&apos;s URL above is used.</p>
            </details>
            {notice && <p role="status" className="text-sm text-emerald-700 dark:text-emerald-300">{notice}</p>}
        </div>
    );
}
